import { useEffect, useState } from 'react';
import {
  queryOptions,
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { useApplication } from 'merchant-api/unified';
import { queryClient } from 'merchant-api';
import { LDR } from 'merchant-api/types';
import { spiceroad } from '..';

export interface UsePlaidProps {
  getClientInfo: Function;
  trackPlaid: Function;
  isLoading: boolean;
  isPlaidConnected: boolean;
  plaidMerchantStatusError: any;
  isLoadingPlaidMerchantStatus: boolean;
  plaidPartnerInfo: any;
  plaidPartnerInfoError: any;
  plaidMerchantStatusQuery: UseQueryResult<PlaidMerchantStatus>;
  plaidPartnerInfoQuery: UseQueryResult<any>;
  trackPlaidMutation: UseMutationResult<any, any, PlaidRequest>;
  getClientInfoMutation: UseMutationResult<any, any, void>;
}

interface PlaidRequest {
  callback: string;
  error: string;
  eventName: string;
  metadata: string;
  publicToken: string;
}

interface PlaidMerchantStatus {
  plaidConnected: boolean;
}

const getPlaidMerchantStatus = (opportunityId: string) =>
  spiceroad
    .get<PlaidMerchantStatus>('/v1/plaid/merchant/status', {
      params: { opportunityId },
    })
    .then((res) => res.data);

const getPlaidPartnerInfo = (opportunityId: string) =>
  spiceroad.get('/v1/partner-info/' + opportunityId).then((res) => res.data);

const postPlaidTrack =
  (isCheckout: boolean, opportunityId?: string) => (data: PlaidRequest) =>
    spiceroad
      .post(
        isCheckout
          ? '/v1/plaid/track/merchant'
          : `/v1/plaid/track/${opportunityId}`,
        data,
      )
      .then((res) => res.data);

const getPlaidClientInfo = () =>
  spiceroad.get('/v1/plaid/client-info').then((res) => res.data);

export const getPlaidMerchantStatusOpts = (opportunityId: string) =>
  queryOptions<PlaidMerchantStatus>({
    queryKey: ['plaidMerchantStatus', opportunityId],
    queryFn: () => getPlaidMerchantStatus(opportunityId),
  });

export const getPlaidPartnerInfoOpts = (opportunityId: string) =>
  queryOptions({
    queryKey: ['plaidPartnerInfo', opportunityId],
    queryFn: () => getPlaidPartnerInfo(opportunityId),
    refetchOnWindowFocus: false,
  });

export function usePlaid(isCheckout?: boolean): UsePlaidProps {
  const { applicationData, isLoadingApplication } = useApplication();
  const [opportunityId, setOpportunityId] = useState<string | undefined>(
    applicationData?.metadata?.sfOpportunityId,
  );

  useEffect(() => {
    if (applicationData?.metadata?.sfOpportunityId) {
      setOpportunityId(applicationData.metadata.sfOpportunityId);
    }
  }, [applicationData]);

  const plaidMerchantStatusQuery = useQuery({
    ...getPlaidMerchantStatusOpts(opportunityId!),
  });

  const plaidPartnerInfoQuery = useQuery({
    ...getPlaidPartnerInfoOpts(opportunityId!),
    enabled: !!opportunityId,
  });

  const trackPlaidMutation = useMutation({
    mutationFn: postPlaidTrack(isCheckout ?? false, opportunityId ?? ''),
    // The intake bank request only closes asynchronously downstream (SF automation), so a
    // successful connection must be authoritative in the cache: without this patch the step
    // mapper still sees an open bank request and bounces the wizard back to BANK_INFO.
    onSuccess: (_data, variables: PlaidRequest) => {
      if (isCheckout || variables.eventName !== 'OPEN') return;
      queryClient.setQueryData<LDR[]>(['ldr', opportunityId], (rows) =>
        rows?.map((r) => (r.issueType === 'No bank statements' && r.status === 'REQUESTED' ? { ...r, status: 'MERCHANT_RESPONSE_SUBMITTED' } : r)),
      );
    },
  });

  const getClientInfoMutation = useMutation({
    mutationFn: getPlaidClientInfo,
  });

  const {
    data: plaidMerchantStatusData,
    error: plaidMerchantStatusError,
    isLoading: isLoadingPlaidMerchantStatus,
  } = plaidMerchantStatusQuery;
  const {
    data: plaidPartnerInfo,
    error: plaidPartnerInfoError,
    isLoading: isLoadingPlaidPartnerInfo,
  } = plaidPartnerInfoQuery;

  return {
    getClientInfo: getClientInfoMutation.mutateAsync,
    isLoading: getClientInfoMutation.isPending,
    trackPlaid: trackPlaidMutation.mutateAsync,
    isPlaidConnected: plaidMerchantStatusData?.plaidConnected ?? false,
    plaidMerchantStatusError: plaidMerchantStatusError,
    isLoadingPlaidMerchantStatus:
      isLoadingPlaidMerchantStatus ||
      (isLoadingApplication && opportunityId == null),
    plaidPartnerInfo:
      isLoadingPlaidPartnerInfo ||
      (isLoadingApplication && opportunityId == null)
        ? undefined
        : Object.keys(plaidPartnerInfo || {}).length
          ? plaidPartnerInfo
          : null,
    plaidPartnerInfoError: plaidPartnerInfoError,
    plaidMerchantStatusQuery,
    plaidPartnerInfoQuery,
    trackPlaidMutation,
    getClientInfoMutation,
  };
}

export default usePlaid;
